import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import GrainOverlay from "@/components/GrainOverlay";
import BlogTopbar from "@/components/BlogTopbar";
import useScrollTop from "@/hooks/useScrollTop";
import { listPosts, listPostTags, resolveImage } from "@/lib/adminApi";

export const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d) ? iso : d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

// Blog listing — one featured post, then an editorial two-column flow.
export default function BlogList() {
  useScrollTop();
  const [params, setParams] = useSearchParams();
  const tag = params.get("tag") || "";
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    document.title = "Blog — Warren Wulkan";
    listPostTags().then(setTags).catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    setLoaded(false);
    listPosts(tag || undefined)
      .then((d) => active && setPosts(d))
      .catch((e) => console.error("posts", e))
      .finally(() => active && setLoaded(true));
    return () => { active = false; };
  }, [tag]);

  const [first, ...rest] = posts;

  return (
    <div className="grid-root blog-root" data-testid="blog-root">
      <GrainOverlay />
      <BlogTopbar />

      <header className="grid-header">
        <p className="grid-overline mono-caps">Notes from the studio</p>
        <h1 className="grid-title">Blog</h1>
        <p className="grid-sub">Process, exhibitions and the slow thinking behind the work.</p>
        {tags.length > 0 && (
          <div className="blog-tags" role="tablist" aria-label="Filter by tag" data-testid="blog-tags">
            <button type="button" role="tab" aria-selected={!tag} className={`blog-tag mono-caps${!tag ? " is-active" : ""}`} onClick={() => setParams({})} data-testid="blog-tag-all">All</button>
            {tags.map((t) => (
              <button key={t} type="button" role="tab" aria-selected={tag === t} className={`blog-tag mono-caps${tag === t ? " is-active" : ""}`} onClick={() => setParams({ tag: t })} data-testid={`blog-tag-${t.replace(/\s+/g, "-")}`}>
                {t}
              </button>
            ))}
          </div>
        )}
      </header>

      <main className="blog-wall" data-loaded={loaded} data-testid="blog-wall">
        {loaded && posts.length === 0 && (
          <p className="grid-empty" data-testid="blog-empty">Nothing written here yet — the first note is on its way.</p>
        )}

        {first && <PostCard p={first} featured />}
        {rest.length > 0 && (
          <div className="blog-grid">
            {rest.map((p) => <PostCard key={p.id} p={p} />)}
          </div>
        )}
      </main>

      <footer className="grid-foot">
        <Link to="/" className="grid-back mono-caps" data-testid="blog-back-to-hall">&larr; Return to the hall</Link>
        <span className="mono-caps grid-foot__meta">{posts.length} post{posts.length === 1 ? "" : "s"}</span>
      </footer>
    </div>
  );
}

function PostCard({ p, featured = false }) {
  return (
    <article className={`post-card${featured ? " post-card--featured" : ""}`} data-testid={`post-card-${p.slug}`}>
      <Link to={`/blog/${p.slug}`} className="post-card__hit" data-cursor="Read">
        {p.image_url && (
          <span className="post-card__media">
            <img src={resolveImage(p.image_url)} alt="" loading={featured ? "eager" : "lazy"} decoding="async" />
          </span>
        )}
        <span className="post-card__text">
          <span className="post-card__meta mono-caps">
            <time dateTime={p.date}>{fmtDate(p.date)}</time>
            {p.author && <><span className="contact__dot">&middot;</span><span>{p.author}</span></>}
          </span>
          <span className="post-card__title">{p.title}</span>
          {p.excerpt && <span className="post-card__excerpt">{p.excerpt}</span>}
          {p.tags?.length > 0 && (
            <span className="post-card__tags mono-caps">{p.tags.join(" · ")}</span>
          )}
          <span className="post-card__more mono-caps">Read &rarr;</span>
        </span>
      </Link>
    </article>
  );
}
